import * as yup from "yup";
import { PaymentMethod } from "../../../types";
import { validateAndNormalizeBdPhone } from "../../../utils/phone";

// Staff-created customers (this form always creates source=ADMIN
// customers, see customer_service.create_customer_without_addresses)
// stay offline-friendly — phone is optional here, unlike the storefront's
// self-registration — but if provided it must be a valid BD number.
// A new customer's address is the order's delivery address (asked once),
// so street/city aren't separate required fields here.
const newCustomerSchema = yup.object({
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().optional(),
    email: yup.string().email("Invalid email").optional(),
    phone: yup
        .string()
        .optional()
        .test(
            "bd-phone",
            "Phone number must start with 01 or +8801",
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    alternative_phone: yup
        .string()
        .optional()
        .test(
            "bd-phone",
            "Phone number must start with 01 or +8801",
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    date_of_birth: yup.string().optional(),
    profile: yup.string().optional(),
    street_address: yup.string().optional(),
    city: yup.string().optional(),
    map_link: yup.string().optional(),
    note: yup.string().optional(),
    marketing_consent: yup.boolean().optional(),
    newsletter_subscription: yup.boolean().optional(),
});

export const orderFormSchema = yup.object({
    customer_id: yup.string().optional(),
    customer_mode: yup.string().oneOf(["select", "create"]).default("select"),
    // New customer fields - only required when in create mode
    new_customer: yup.object().when("customer_mode", {
        is: "create",
        then: () => newCustomerSchema,
        otherwise: (s) => s.optional(),
    }),
    status: yup.string().required("Status is required"),
    shipping_address: yup.string().required("Enter the delivery address"),
    shipping_city: yup.string().required("Enter the city"),
    delivery_area_id: yup.string().optional(),
    delivery_service_id: yup.string().optional(),
    order_channel_id: yup.string().optional(),
    notes: yup.string().optional(),
    show_gifts_on_bill: yup.boolean().default(true),
    shipping_amount: yup
        .number()
        .typeError("Enter an amount")
        .min(0, "Can't be negative")
        .default(0),
    discount_amount: yup
        .number()
        .typeError("Enter an amount")
        .min(0, "Can't be negative")
        .default(0),
    delivery_cost: yup
        .number()
        .typeError("Enter an amount")
        .min(0, "Can't be negative")
        .default(0),
    items: yup
        .array()
        .of(
            yup.object({
                product_id: yup.string().optional(),
                quantity: yup
                    .number()
                    .typeError("Enter a quantity")
                    .min(1, "At least 1")
                    .optional(),
                unit_price: yup
                    .number()
                    .typeError("Enter a price")
                    .min(0, "Can't be negative")
                    .optional(),
                is_gift: yup.boolean().optional(),
            }),
        )
        .min(0),
});

export type OrderStatus = "DRAFT" | "PENDING" | "CONFIRMED" | "SHIPPED" | "DELIVERED" | "CANCELLED";

export interface OrderFormLine {
    item_id?: string; // For existing items in edit mode
    product_id: string;
    quantity: number;
    unit_price: number;
    is_gift?: boolean;
}

export interface OrderFormData {
    customer_id: string;
    customer_mode: "select" | "create";
    new_customer?: {
        first_name: string;
        last_name?: string;
        email?: string;
        phone?: string;
        alternative_phone?: string;
        date_of_birth?: string;
        profile?: string;
        street_address?: string;
        city?: string;
        map_link?: string;
        is_active?: boolean;
        marketing_consent?: boolean;
        newsletter_subscription?: boolean;
        note?: string;
    };
    status: OrderStatus;
    shipping_address: string;
    shipping_city: string;
    delivery_area_id?: string;
    delivery_service_id?: string;
    order_channel_id?: string;
    notes?: string;
    show_gifts_on_bill: boolean;
    shipping_amount: number;
    discount_amount: number;
    delivery_cost: number;
    items: OrderFormLine[];
}

export interface PaymentDraft {
    amount: number;
    payment_method: PaymentMethod;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
    /** Set on a payment that's already saved — kept through edits. */
    transaction_id?: string;
}

/** Read-only notes from a JSON import, shown next to the matching field. */
export interface ImportHints {
    delivery?: string;
    shipping?: string;
    items?: Record<number, string>;
}

/** Items total of the form's lines (gift lines are priced at 0). */
export const itemsTotalOf = (items: Pick<OrderFormLine, "quantity" | "unit_price">[]): number =>
    Math.round(
        items.reduce((total, item) => total + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0) * 100,
    ) / 100;

/** "01711-223344" or "+880 1711…" → a phone; anything else → a name. */
export function splitTypedCustomer(text: string): { first_name: string; last_name: string; phone: string } {
    const typed = text.trim();
    if (/^[+\d][\d\s-]{6,}$/.test(typed)) {
        return { first_name: "", last_name: "", phone: typed.replace(/[\s-]/g, "") };
    }
    const [first = "", ...rest] = typed.split(/\s+/);
    return { first_name: first, last_name: rest.join(" "), phone: "" };
}
